import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";

interface AdminAuthContextValue {
  email: string | null;
  isLoading: boolean;
  requestCode: (email: string, turnstileToken: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch("/api/admin/auth/me", { credentials: "include" })
      .then((r) => r.json())
      .then((json: { ok: boolean; data?: { email: string } }) => {
        if (json.ok && json.data) setEmail(json.data.email);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const requestCode = useCallback(async (emailArg: string, turnstileToken: string) => {
    const res = await fetch("/api/admin/auth/request-code", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailArg, turnstileToken }),
    });
    const json = await res.json() as { ok: boolean; error?: string };
    if (!json.ok) throw new Error(json.error ?? "Kod gönderilemedi");
  }, []);

  const verifyCode = useCallback(async (emailArg: string, code: string) => {
    const res = await fetch("/api/admin/auth/verify-code", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailArg, code }),
    });
    if (!res.ok) throw new Error(`Auth failed: ${res.status}`);
    const json = await res.json() as { ok: boolean; data?: { email: string }; error?: string };
    if (!json.ok) throw new Error(json.error ?? "Doğrulama başarısız");
    if (json.data) setEmail(json.data.email);
  }, []);

  const logout = useCallback(async () => {
    const res = await fetch("/api/admin/auth/logout", { method: "POST", credentials: "include" });
    if (!res.ok) console.warn(`Logout request failed: ${res.status}`);
    setEmail(null);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ email, isLoading, requestCode, verifyCode, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
